/**
 * ONE shared read of today's prepared day (defect fix 2026-08-29)
 * ===============================================================
 *
 * Defect: /today fetched /api/attention/v1/prepared-day two to three times per
 * load, from independent components with different query params — the masthead
 * "N/M plan taken" counter with `?build=0`, and the curtain unparameterised
 * (which BUILDS). Besides the duplicate traffic that is a race: the passive
 * counter could read a plan the curtain was about to cause to be built, and
 * report a plan that no longer matches the one on screen.
 *
 * Shape: every PASSIVE consumer (a counter, a subordination check) reads this
 * module and shares a single in-flight `?build=0` request; the BUILDING call
 * stays owned by the curtain, which is the surface that acts on the plan.
 *
 * `status` is explicit on purpose. A failed request is NOT an empty plan —
 * consumers must be able to tell "no live entries" from "we could not look",
 * so nothing renders an empty state (or a competing proposal) on a failure.
 *
 * Lives under components/prioritization/ because that is where its two
 * consumers live today; it belongs in services/ once the curtain (owned
 * elsewhere) adopts it as its own loader.
 */

import { useEffect, useSyncExternalStore } from 'react'
import type { PreparedDay, PreparedEntry } from '../../services/preparedDayService'

// Mirrors the curtain's own liveness filter — an entry the user has already
// dealt with (or that went stale) is not something still being offered.
const NOT_LIVE = ['CONSUMED', 'STALE', 'ADOPTED', 'SKIPPED_FOR_DAY']

export interface PreparedDaySnapshot {
  /** 'loading' until the first response; 'error' when the request failed. */
  status: 'loading' | 'ready' | 'error'
  /** A plan for the user-day is already built. */
  exists: boolean
  /** The attention feature is off for this user — no plan is ever coming. */
  disabled: boolean
  /** Entries still on offer (the curtain's own liveness rule). */
  liveCount: number
  /**
   * The item ids behind those live entries.
   *
   * Exposed so a caller can tell whether an option is ALREADY on today's plate.
   * The curtain and the focus list draw from the same pool, so on 2026-09-08 the
   * curtain was offering the exact task "Start here" was telling the person to
   * do — "Next in line · Start this" above, "hasn't had attention lately ·
   * Adopt" below.
   */
  liveItemIds: number[]
  /** ACTION entries the user has taken today, and how many were prepared. */
  actionsTaken: number
  actionsTotal: number
}

const LOADING: PreparedDaySnapshot = {
  status: 'loading', exists: false, disabled: false,
  liveCount: 0, liveItemIds: [], actionsTaken: 0, actionsTotal: 0,
}
const FAILED: PreparedDaySnapshot = {
  status: 'error', exists: false, disabled: false,
  liveCount: 0, liveItemIds: [], actionsTaken: 0, actionsTotal: 0,
}

function summarize(body: PreparedDay): PreparedDaySnapshot {
  const entries: PreparedEntry[] = Array.isArray(body?.entries) ? body.entries : []
  const base = { status: 'ready' as const, exists: !!body?.exists, disabled: !!body?.disabled }
  // `exists: false` from a ?build=0 read means "not built YET" — the curtain's
  // own (building) call is what creates it. Never read that as an empty plan.
  if (!base.exists) return { ...base, liveCount: 0, liveItemIds: [], actionsTaken: 0, actionsTotal: 0 }
  const actions = entries.filter(e => e.entry_role === 'ACTION')
  const live = entries.filter(e => !NOT_LIVE.includes(e.day_state))
  return {
    ...base,
    liveCount: live.length,
    liveItemIds: live
      .map(e => e.canonical_item_id)
      .filter((id): id is number => typeof id === 'number'),
    actionsTaken: actions.filter(
      e => e.day_state === 'ADOPTED' || e.day_state === 'CONSUMED').length,
    actionsTotal: actions.length,
  }
}

let current: PreparedDaySnapshot = LOADING
let servedVersion = -1      // highest version `current` (or the in-flight call) covers
let inflight = false
let pending = false         // a newer version arrived mid-flight
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

function start(): void {
  inflight = true
  fetch('/api/attention/v1/prepared-day?build=0', { credentials: 'include' })
    .then(r => (r.ok ? r.json() : null))
    .then((b: PreparedDay | null) => { current = b ? summarize(b) : FAILED })
    .catch(() => { current = FAILED })
    .finally(() => {
      inflight = false
      emit()
      if (pending) { pending = false; start() }
    })
}

/** Load once per version; concurrent mounts (StrictMode included) share it. */
function ensureLoaded(version: number): void {
  const stale = version > servedVersion || (current.status === 'loading' && !inflight)
  if (!stale) return
  servedVersion = Math.max(servedVersion, version)
  if (inflight) { pending = true; return }
  start()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/**
 * Read today's prepared day. `version` is a monotonic counter the caller bumps
 * when it knows the plan changed (a refresh, an adoption); passive readers that
 * have no such signal pass nothing and ride along on whatever the others load.
 */
export function usePreparedDaySnapshot(version = 0): PreparedDaySnapshot {
  useEffect(() => { ensureLoaded(version) }, [version])
  return useSyncExternalStore(subscribe, () => current)
}

/**
 * True only when the prepared-day curtain — the PRIMARY "what should I work
 * on" selector — has nothing on offer, so a second selector may speak without
 * contradicting it. Deliberately false while loading, on an error, and while
 * a plan simply has not been built yet: none of those PROVE the curtain is
 * absent, and two answers to one question is worse than none.
 */
export function curtainIsSilent(s: PreparedDaySnapshot): boolean {
  if (s.status !== 'ready') return false
  if (s.disabled) return true
  return s.exists && s.liveCount === 0
}
